/* Brief 3.5 PR 7-A — mechanical file split. Lifted verbatim from
   AdminMerchantDetail.jsx so the lint gate (PR 7-B) runs on small,
   focused files. No behaviour change. */
import { useEffect, useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronRight, Eye, Loader2, MinusCircle, ShieldAlert, Sparkles, X } from "lucide-react";
import api from "../../../lib/api";
import { plainStatus } from "../../../lib/plainLanguage";
import { Section, fmtDate } from "./primitives";

// ---------- E. Decision interface (Brief 3.6.2) ----------

const STATUS_PRESENTATION = {
  approved: { label: "Approve", icon: CheckCircle2, tone: "emerald", danger: false,
              helper: "Move to approved. Admin will follow up with API key handoff." },
  under_review: { label: "Mark under review", icon: Eye, tone: "cyan", danger: false,
                  helper: "Pull this application into active underwriting." },
  info_requested: { label: "Request more info", icon: AlertCircle, tone: "amber", danger: false,
                    helper: "Pause review and ask the merchant for specific items." },
  declined: { label: "Decline", icon: MinusCircle, tone: "rose", danger: true,
              helper: "Decline this application. Reason will be shown to the merchant." },
  live: { label: "Mark live", icon: Sparkles, tone: "emerald", danger: false,
          helper: "Promote to live. Use after API-key handoff (3.6.3)." },
  draft: { label: "Reopen as draft", icon: Eye, tone: "cyan", danger: false,
           helper: "Restore the application to draft state so the merchant can continue editing." },
};

// Override labels based on the current state. e.g. "Mark under review"
// becomes "Reopen for review" when coming FROM cancelled/declined.
function presentationFor(fromStatus, toStatus) {
  const base = STATUS_PRESENTATION[toStatus] || { label: toStatus, icon: ChevronRight, tone: "cyan", danger: false };
  if ((fromStatus === "cancelled" || fromStatus === "declined") && toStatus === "under_review") {
    return { ...base, label: "Reopen for review",
             helper: `Restore this application from ${fromStatus}. A reason ≥ 20 chars is required.` };
  }
  return base;
}

const TONE_CLS = {
  emerald: "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-200 hover:bg-emerald-400/[0.12]",
  cyan: "border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-200 hover:bg-cyan-400/[0.12]",
  amber: "border-amber-400/30 bg-amber-400/[0.06] text-amber-200 hover:bg-amber-400/[0.12]",
  rose: "border-rose-400/30 bg-rose-400/[0.06] text-rose-200 hover:bg-rose-400/[0.12]",
};

export function DecisionPanel({ identity, merchantId, onTransitioned }) {
  const allowed = identity.allowed_next_statuses || [];
  const [openModal, setOpenModal] = useState(null); // {to_status} | null

  if (allowed.length === 0) {
    return (
      <Section icon={Sparkles} title="Decision" testId="merchant-section-decision">
        <div className="rounded-xl border border-dashed border-white/[0.08] bg-white/[0.01] p-5 text-[12.5px] text-zinc-500">
          No further admin actions available from <span className="text-zinc-300">{plainStatus(identity.status)}</span>.
        </div>
      </Section>
    );
  }

  return (
    <Section icon={Sparkles} title="Decision" testId="merchant-section-decision">
      <div className="text-[12px] text-zinc-500 mb-3">
        Current status: <span className="text-zinc-200" data-testid="decision-panel-current-status">{plainStatus(identity.status)}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {allowed.map((to) => {
          const cfg = presentationFor(identity.status, to);
          const Icon = cfg.icon;
          return (
            <button
              key={to}
              type="button"
              onClick={() => setOpenModal({ to_status: to })}
              data-testid={`merchant-decision-${to}-btn`}
              title={cfg.helper}
              className={`inline-flex items-center gap-1.5 rounded-full border ${TONE_CLS[cfg.tone] || TONE_CLS.cyan} px-4 py-2 text-[12.5px] transition-colors`}
            >
              <Icon className="h-3.5 w-3.5" /> {cfg.label}
            </button>
          );
        })}
      </div>

      {openModal && (
        <DecisionModal
          merchantId={merchantId}
          currentStatus={identity.status}
          toStatus={openModal.to_status}
          onClose={() => setOpenModal(null)}
          onSuccess={() => { setOpenModal(null); onTransitioned?.(); }}
        />
      )}
    </Section>
  );
}


function DecisionModal({ merchantId, currentStatus, toStatus, onClose, onSuccess }) {
  const cfg = STATUS_PRESENTATION[toStatus] || {};
  const isDecline = toStatus === "declined";
  const isUnDecline = currentStatus === "declined" && toStatus === "under_review";
  const requiresReason = isDecline || isUnDecline;
  const isInfoRequest = toStatus === "info_requested";

  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([""]);
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const reasonValid = !requiresReason || reason.trim().length >= 20;
  const cleanedItems = items.map((s) => s.trim()).filter(Boolean);
  const infoSignalPresent = !isInfoRequest ||
    (categories.length > 0 || cleanedItems.length > 0 || freeText.trim().length > 0);
  const canSubmit = !submitting && reasonValid && infoSignalPresent;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const body = {
        to_status: toStatus,
        expected_current_status: currentStatus,
      };
      if (notes.trim()) body.notes = notes.trim();
      if (requiresReason) body.reason = reason.trim();
      if (isInfoRequest) {
        body.info_request = {
          categories,
          items: cleanedItems,
          free_text: freeText.trim() || null,
        };
      }
      await api.post(`/admin/merchants/${merchantId}/status`, body);
      onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError(
        status === 409 ? `Status changed since this page loaded — please reload. (${detail || ""})` :
        status === 422 ? (detail || "Validation failed.") :
        status === 400 ? (detail || "Transition not allowed from current status.") :
        "Could not apply the decision. Please try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
      data-testid="decision-modal-overlay"
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Confirm ${cfg.label || toStatus}`}
        data-testid="decision-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Decision</div>
            <div className={`text-white text-[16px] mt-1 ${cfg.danger ? "text-rose-200" : ""}`}>
              {cfg.label || toStatus}
            </div>
            <div className="text-zinc-500 text-[12px] mt-1">
              {currentStatus} → <span className="text-zinc-300">{toStatus}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="decision-modal-close"
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>

        {requiresReason && (
          <div className="mb-4">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
              Reason {isUnDecline ? "(recovery rationale)" : ""} — minimum 20 characters
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              autoFocus
              data-testid="decision-modal-reason"
              placeholder={isDecline
                ? "Why is this application being declined? The merchant will see this."
                : "Why is this merchant being recovered from declined?"}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/20"
            />
            <div className={`mt-1 text-[11px] ${reasonValid ? "text-zinc-500" : "text-amber-300"}`}>
              {reason.trim().length} / 20 minimum
            </div>
          </div>
        )}

        {isInfoRequest && (
          <div className="mb-4 space-y-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Categories</label>
              <div className="flex flex-wrap gap-1.5">
                {["documents", "form_data", "verification", "other"].map((c) => {
                  const active = categories.includes(c);
                  return (
                    <button
                      key={c} type="button"
                      onClick={() => setCategories(active
                        ? categories.filter((x) => x !== c)
                        : [...categories, c])}
                      data-testid={`info-request-category-${c}`}
                      className={`rounded-full border px-3 py-1 text-[11.5px] transition-colors ${
                        active
                          ? "border-amber-400/40 bg-amber-400/[0.10] text-amber-100"
                          : "border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:border-white/[0.14] hover:text-zinc-200"
                      }`}
                    >{c}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Specific items requested</label>
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5">
                  <input
                    type="text"
                    value={it}
                    onChange={(e) => {
                      const next = [...items];
                      next[i] = e.target.value;
                      setItems(next);
                    }}
                    maxLength={200}
                    data-testid={`info-request-item-${i}`}
                    placeholder={i === 0 ? "e.g. bank statement (last 3 months)" : "another item"}
                    className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/20"
                  />
                  {items.length > 1 && (
                    <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))}
                            data-testid={`info-request-item-remove-${i}`}
                            className="text-zinc-500 hover:text-rose-300">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {items.length < 20 && (
                <button type="button" onClick={() => setItems([...items, ""])}
                        data-testid="info-request-item-add"
                        className="text-[11.5px] text-cyan-300 hover:text-cyan-200">
                  + Add another item
                </button>
              )}
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Free-text note</label>
              <textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                rows={3}
                maxLength={2000}
                data-testid="info-request-free-text"
                placeholder="Any additional context the merchant should know."
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/20"
              />
            </div>
            {!infoSignalPresent && (
              <div className="text-[11px] text-amber-300" data-testid="info-request-needs-signal">
                Add at least one category, item, or free-text note before continuing.
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={2000}
            data-testid="decision-modal-notes"
            placeholder="Internal notes — visible to other admins, not the merchant."
            className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/20"
          />
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200" data-testid="decision-modal-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="decision-modal-cancel"
                  className="rounded-full border border-white/[0.08] bg-white/[0.01] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit}
                  data-testid="decision-modal-confirm"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[12.5px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${TONE_CLS[cfg.tone] || TONE_CLS.cyan}`}>
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Confirm {cfg.label || toStatus}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DecisionBanner({ kind, payload }) {
  if (kind === "decline") {
    return (
      <div className="mb-4 rounded-xl border border-rose-400/30 bg-rose-400/[0.06] px-4 py-3" data-testid="decision-banner-decline">
        <div className="flex items-start gap-2">
          <MinusCircle className="h-4 w-4 text-rose-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] uppercase tracking-[0.18em] text-rose-300/80">Declined</div>
            <div className="text-rose-100 text-[13px] mt-1 whitespace-pre-wrap break-words">{payload.reason}</div>
            <div className="text-rose-300/60 text-[11px] mt-1">
              by {payload.declined_by} · {fmtDate(payload.declined_at)}
            </div>
          </div>
        </div>
      </div>
    );
  }
  // kind === "info_request"
  const cats = payload.categories || [];
  const items = payload.items || [];
  return (
    <div className="mb-4 rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3" data-testid="decision-banner-info-request">
      <div className="flex items-start gap-2">
        <AlertCircle className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] uppercase tracking-[0.18em] text-amber-300/80">Awaiting merchant response</div>
          {cats.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {cats.map((c) => (
                <span key={c} className="rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-2 py-0.5 text-[11px] text-amber-100">{c}</span>
              ))}
            </div>
          )}
          {items.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-[12.5px] text-amber-50 space-y-0.5">
              {items.map((it, i) => <li key={i} className="break-words">{it}</li>)}
            </ul>
          )}
          {payload.free_text && (
            <div className="mt-2 text-[12.5px] text-amber-50 whitespace-pre-wrap break-words">{payload.free_text}</div>
          )}
          <div className="text-amber-300/60 text-[11px] mt-2">
            requested by {payload.requested_by} · {fmtDate(payload.requested_at)}
          </div>
        </div>
      </div>
    </div>
  );
}
